/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Starting-project templates for the case-study cards. Each entry maps a
// case-study `id` to a minimal setup that gets the user into the right
// chamber with the right starting structure loaded. The case-study text
// itself lives in i18n (caseStudiesPage.studies.<id>) — this file only
// defines the *executable* part.
//
// Flow:
//   1. User clicks "Try this idea" on a card → navigateToTemplate(id)
//      sets ?template=<id> in the URL and navigates to the main app.
//   2. AppCreator's first-load effect picks up the param and calls
//      runTemplate(id), which sets projectType + loads the asset.
//   3. The param is removed from the URL so a refresh doesn't re-trigger.
//
// Not every case study has a template — entries we can build today live
// here; the rest read as "Coming soon" on the page. Adding a template
// means adding an entry below + bundling the asset (or wiring a fetch).

import { GalleryViewMode, ProjectType } from '../constants.ts';
import { useStore } from '../stores/common.ts';
import { clearUndoHistory } from '../undo/setWithUndo.ts';
import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { targetProteins, monatomicMolecules, commonMolecules } from '../internalDatabase.ts';
import { fetchPdbText, normalizePdbId, pdbUrl } from '../view/rcsbFetch.ts';
import { lookupInternalCrystal } from '../crystal/crystalLookup.ts';
import { takenCrystalNames, uniqueCrystalName } from '../crystal/Crystal.ts';
import { loadMolecule } from '../view/moleculeTools.ts';
import { ProjectUtil } from '../project/ProjectUtil.ts';
import type { CrystalState, MoleculeInterface } from '../types.ts';

// Protein template — loads a structure into projectState.protein and
// switches to the docking chamber. `pdbId` triggers an RCSB fetch;
// `internalProteinName` looks up a bundled PDB from targetProteins.
interface ProteinTemplate {
  kind: 'protein';
  pdbId?: string;
  internalProteinName?: string;
}

// Crystal-preset template — looks up a preset by name (synchronous, no
// network) and seeds the gallery + selected crystal. Switches to the
// Crystal Builder chamber. Optionally drops a guest molecule into the
// chamber (a clathrate-style guest in a periodic host) so the user lands
// straight on a "guest in a porous structure" scene without having to
// open the Guests popover manually.
interface CrystalPresetTemplate {
  kind: 'crystal-preset';
  presetQuery: string; // passed to lookupInternalCrystal
  // Short key from models/crystal/crystalGuests.ts — 'CH4', 'H2', 'Ar',
  // 'CO2', etc. Caller is responsible for picking one whose kinetic
  // diameter actually fits the host's cavities, otherwise the LJ wall
  // pushes the guest out.
  guestKey?: string;
  // When true, the template opens with NVT (constant-temperature) MD at
  // `targetTemperatureK` using the project's default thermostat. Without
  // this, MD defaults to NVE — fine for energy-conservation demos but
  // wrong for "let the guest find a low-energy site" workflows where we
  // want the guest to relax to an equilibrium pose.
  nvt?: boolean;
  targetTemperatureK?: number;
}

// Gas template — a Molecular Modeling scene set up for gas-law experiments: fill
// the container with many copies of a bundled monatomic/diatomic gas and enable
// the piston barostat + NVT thermostat. `species` is an internal-database name
// (e.g. 'Argon', 'Dinitrogen'). The project opens pre-titled so a Save lands it
// under that name.
interface GasTemplate {
  kind: 'gas';
  species: string;
  count: number;
  title: string;
  boxX: number;
  boxY: number;
  boxZ: number;
  temperatureK: number;
  pistonPressureMPa: number;
  timeStepFs: number; // a monatomic LJ gas has no stiff bonds, so a larger step is stable
  refreshInterval: number; // MD steps per frame — a tiny gas is cheap, so run many per frame
}

// Nanostructure template — cuts a quantum dot / nanoparticle out of a preset
// crystal by geometry (no AI, like the agent's buildNanostructure tool) and
// opens a Nanomaterial Design project with it in the gallery and the lab,
// chat view in front: every nanostructure tool (energy levels, size sweeps,
// melting) is reached through the chat.
interface NanostructureTemplate {
  kind: 'nanostructure';
  hostPreset: string; // passed to lookupInternalCrystal
  shape: 'sphere' | 'cube' | 'octahedron' | 'cuboctahedron';
  diameterNm: number;
  // The lab opens thermostatted at this temperature (NVT), so the dot sits at
  // room temperature instead of cooling under NVE as its surface relaxes.
  temperatureK: number;
}

type Template = ProteinTemplate | CrystalPresetTemplate | GasTemplate | NanostructureTemplate;

// Keys must match the `id` field on CASE_STUDIES in caseStudies.tsx.
// Comments mirror the case-study titles so reviewers can match without
// jumping files.
const TEMPLATES: Record<string, Template> = {
  // s1 — Non-peptide HIV-1 protease inhibitor optimization (bundled 1AID)
  s1: { kind: 'protein', internalProteinName: 'HIV-1 Protease' },
  // s2 — Pocket-aware kinase inhibitor design (cAMP-dependent kinase 1ATP)
  s2: { kind: 'protein', pdbId: '1ATP' },
  // s3 — Antibiotic scaffold revival against MRSA (PBP2a, 4DI7)
  s3: { kind: 'protein', pdbId: '4DI7' },
  // s4 — P-doped silicon for thin-film photovoltaics (Silicon preset)
  s4: { kind: 'crystal-preset', presetQuery: 'Silicon' },
  // s6 — Guest molecule adsorption in a porous crystal. Uses cristobalite
  // (porous silica polymorph, already in presets) as the host since AIMS
  // doesn't yet ship a sodalite or other proper zeolite preset. H2 is
  // small enough (kinetic diameter ~2.9 Å) to fit the cristobalite
  // interstitial channels and not get expelled by the LJ wall.
  // NVT at 300 K so the guest thermalizes and drifts into a low-energy
  // site rather than running away under NVE drift.
  s6: { kind: 'crystal-preset', presetQuery: 'Cristobalite', guestKey: 'H2', nvt: true, targetTemperatureK: 300 },
  // s7 — Tubulin polymerization inhibitor (bundled 1TUB)
  s7: { kind: 'protein', internalProteinName: 'Tubulin' },
  // s9 — KRAS G12C covalent inhibitor (PDB 6OIM, sotorasib-bound KRAS).
  // The structure of the year (decade?) for oncology — first crystal showing
  // a covalent inhibitor in the switch-II cryptic pocket. RCSB fetch.
  s9: { kind: 'protein', pdbId: '6OIM' },
  // s11 — GLP-1 receptor with a small-molecule agonist bound (PDB 7LCI).
  // Family-B GPCR; the most active drug-discovery target class of 2024-26
  // (Ozempic / Mounjaro / Zepbound). RCSB fetch.
  s11: { kind: 'protein', pdbId: '7LCI' },
  // gaslaws — Gas Laws: 200 argon atoms in a tall box under the piston barostat
  // at ~1 atm, NVT 300 K. Argon is a monatomic ideal/LJ gas, so P·V ≈ N·k·T;
  // sweep pressure (Boyle) or temperature (Charles) and read the Volume tab.
  gaslaws: {
    kind: 'gas',
    species: 'Argon',
    count: 200,
    title: 'Gas Laws',
    boxX: 80,
    boxY: 80,
    boxZ: 120,
    // ~2 MPa balances 200 argon at 300 K near mid-box (P_gas = NkT/V), so the
    // plate settles INSIDE the container with room to move — 0.1 MPa (1 atm) is
    // ~10× below the gas's own pressure at this density and pins the plate at the
    // ceiling.
    temperatureK: 300,
    pistonPressureMPa: 2,
    timeStepFs: 5,
    refreshInterval: 100,
  },
  // s13 — Cadmium-free quantum dot for a display pixel: a 2.4 nm indium
  // phosphide dot, the size that emits green in the tight-binding model (the
  // example "Choosing a Cadmium-Free Quantum Dot" measures ~105 nm of color
  // shift per nm of diameter there). Cut from the Indium Phosphide preset.
  s13: { kind: 'nanostructure', hostPreset: 'Indium Phosphide', shape: 'sphere', diameterNm: 2.4, temperatureK: 300 },
};

export const hasTemplate = (id: string): boolean => id in TEMPLATES;

// Synchronous peek used by the primitive store init so showLanding can
// start `false` when the user arrived via a template link — prevents a
// one-frame flash of the landing before the template loader fires.
export const hasTemplateInUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const id = new URLSearchParams(window.location.search).get('template');
    return id !== null && id in TEMPLATES;
  } catch {
    return false;
  }
};

// Navigate to the main app with the template ID baked into the URL. We
// don't load here — the case-studies page is a standalone route, and the
// loader needs to run inside AppCreator where the relevant chambers and
// store wiring are mounted.
export const navigateToTemplate = (id: string) => {
  if (!hasTemplate(id)) return;
  const base = import.meta.env.BASE_URL || '/';
  const url = `${base}?template=${encodeURIComponent(id)}`;
  window.location.assign(url);
};

// Run the template setup. Called from AppCreator's first-load effect.
// Best-effort: errors are logged and the loader silently bails so the
// user sees whatever landing/chamber state is left, not a crash.
//
// We RESET projectState to defaults first (so the user doesn't see the
// previously-opened project's title / owner / timestamp lingering in the
// chrome) and then apply the template setup on top. The template still
// looks like a brand-new untitled project until the user explicitly saves
// it via Save As — at which point Firestore writes happen.
export const runTemplate = async (id: string): Promise<void> => {
  const tpl = TEMPLATES[id];
  if (!tpl) return;
  // Reset project + dismiss landing in one set, so the previous project's
  // title (etc.) is gone before the chamber renders.
  useStore.getState().set((state) => {
    state.projectState = ProjectUtil.createDefaultProjectState();
  });
  clearUndoHistory();
  usePrimitiveStore.getState().set((s) => {
    s.showLanding = false;
    // Clear any "changed" flag from the previous project so the new
    // template doesn't immediately look "unsaved" before the user has
    // touched it.
    s.changed = false;
  });

  if (tpl.kind === 'protein') {
    await loadProteinTemplate(tpl);
  } else if (tpl.kind === 'crystal-preset') {
    loadCrystalPresetTemplate(tpl);
  } else if (tpl.kind === 'gas') {
    await loadGasTemplate(tpl);
  } else if (tpl.kind === 'nanostructure') {
    await loadNanostructureTemplate(tpl);
  }
};

// Gas-laws scene: set up a Molecular Modeling project, add the bundled gas atom
// to the gallery (parsing it into molecularStructureMap), then hand off to
// lab.tsx to fill the container with `count` non-overlapping copies. Mirrors the
// loadMoleculeToLab tool's flow (gallery molecule → pendingChamberMolecule*),
// but bypasses that tool's 50-copy schema cap since the lab placement itself is
// uncapped.
const loadGasTemplate = async (tpl: GasTemplate): Promise<void> => {
  const setCommonStore = useStore.getState().set;
  const base =
    monatomicMolecules.find((m) => m.name === tpl.species) ?? commonMolecules.find((m) => m.name === tpl.species);
  if (!base) {
    console.warn('Gas template: species not found in the internal database', tpl.species);
    return;
  }
  // Configure the Molecular Modeling scene FIRST so the lab mounts and the box /
  // NVT thermostat / piston barostat are in place before the copies drop.
  setCommonStore((state) => {
    const ps = state.projectState;
    ps.type = ProjectType.MOLECULAR_MODELING;
    ps.title = tpl.title;
    // Open straight into chat (the AI-first surface) with the gallery property
    // graph collapsed — this scene is about watching the piston, not the cards.
    ps.galleryViewMode = GalleryViewMode.CHAT;
    ps.propertiesPaneCollapsed = true;
    ps.molecularContainer = { ...ps.molecularContainer, lx: tpl.boxX, ly: tpl.boxY, lz: tpl.boxZ };
    ps.constantTemperature = true;
    ps.temperature = tpl.temperatureK;
    ps.timeStep = tpl.timeStepFs;
    ps.refreshInterval = tpl.refreshInterval;
    // A LIGHT plate (~one argon mass, vs the auto ~32,000) so its motion is
    // clearly visible: plate mass sets the TIMESCALE of the piston's motion, not
    // its mean position or fluctuation amplitude (those are fixed by the force
    // balance and equipartition), so a lighter plate simply moves faster and reads
    // as livelier. Langevin damping keeps it from rattling on individual
    // collisions. The gas-law SWEEP tools override to their own light, near-critically damped plate
    // during their (headless) measurements, then restore this value.
    ps.pistonEnabled = true;
    ps.pistonPressure = tpl.pistonPressureMPa;
    ps.pistonMass = 40;
    ps.pistonLangevin = true;
  });
  // Add the gas atom to the gallery. loadMolecule fetches + parses the bundled
  // structure (populating molecularStructureMap, which the placement needs) and
  // its callback adds it to projectState.molecules.
  const mol = { ...base } as MoleculeInterface;
  await new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    loadMolecule(mol, () => {
      useStore.getState().addMolecule(mol);
      done();
    });
    // Safety net: never hang the loader if the parse callback doesn't fire.
    setTimeout(done, 4000);
  });
  // Fill the lab with `count` copies. lab.tsx's placement effect consumes the
  // pendingChamberMolecule* flags, spaces the copies non-overlapping, seeds
  // velocities, and grows the box to fit — no copy cap at this layer. It fires
  // only on a flag CHANGE and bails (clearing the flag) if the molecule's parsed
  // structure isn't cached yet OR the lab hasn't mounted — both of which race
  // with template startup. So RE-ARM the flag on a short poll until the copies
  // actually land (or we give up), each attempt re-triggering the effect.
  const arm = () => {
    usePrimitiveStore.getState().set((s) => {
      s.pendingChamberMoleculeName = mol.name;
      s.pendingChamberMoleculeCount = tpl.count;
      s.pendingChamberMoleculeRegion = null;
      s.pendingChamberMoleculePoint = null;
      // Scatter the gas uniformly across the whole container (fill it) rather than
      // clustering the atoms in the middle.
      s.pendingChamberMoleculeScatter = true;
    });
  };
  for (let attempt = 0; attempt < 40; attempt++) {
    if (useStore.getState().projectState.testMolecules.length >= tpl.count) break;
    // Only arm once the parsed structure is actually cached (keyed by the gallery
    // name); otherwise the effect would just clear the flag as a no-op.
    if (useStore.getState().molecularStructureMap.get(mol.name)?.atoms?.length) arm();
    await new Promise((r) => setTimeout(r, 150));
  }
  usePrimitiveStore.getState().setChanged(true);
};

const loadProteinTemplate = async (tpl: ProteinTemplate): Promise<void> => {
  const setCommonStore = useStore.getState().set;
  // Switch to docking chamber up front so the user sees the right empty
  // chamber while we fetch.
  setCommonStore((state) => {
    state.projectState.type = ProjectType.DRUG_DISCOVERY;
  });

  // Bundled-protein path: fetch the local asset's text and inline it the
  // same way dockingSettings.loadFromPdbId does for RCSB fetches. We
  // inline so projectState round-trips through persist without depending
  // on the asset URL surviving a build hash change.
  if (tpl.internalProteinName) {
    const entry = targetProteins.find((p) => p.name === tpl.internalProteinName) as
      | (MoleculeInterface & { url: string })
      | undefined;
    if (!entry) return;
    try {
      const text = await fetch(entry.url).then((r) => r.text());
      setCommonStore((state) => {
        state.projectState.protein = {
          name: entry.name ?? tpl.internalProteinName!,
          url: entry.url,
          data: text,
          internal: true,
        } as MoleculeInterface;
      });
    } catch (e) {
      console.warn('Template: failed to load bundled protein', tpl.internalProteinName, e);
    }
    return;
  }

  // External RCSB path — mirrors dockingSettings.loadFromPdbId.
  if (tpl.pdbId) {
    const id = normalizePdbId(tpl.pdbId);
    const result = await fetchPdbText(id);
    if (!result.ok) {
      console.warn('Template: RCSB fetch failed for', id, result.reason);
      return;
    }
    setCommonStore((state) => {
      state.projectState.protein = {
        name: id,
        url: pdbUrl(id),
        data: result.text,
        internal: false,
      } as MoleculeInterface;
    });
  }
};

const loadCrystalPresetTemplate = (tpl: CrystalPresetTemplate): void => {
  const hit = lookupInternalCrystal(tpl.presetQuery);
  if (!hit) {
    console.warn('Template: crystal preset not found', tpl.presetQuery);
    return;
  }
  const fresh: CrystalState = { ...hit.crystal, source: hit.sourceLabel };
  // Add to gallery + select + mark as chamber crystal so the user sees the
  // crystal both in the gallery list and the chamber. Mirrors what the
  // agent's lookupCrystal tool does in agent/crystalTools.ts. Optionally
  // also seed `chamberGuests` so a "guest in a porous structure" template
  // (s6) opens with the guest already inserted at the chamber center —
  // the user can drag it into a cavity with the on-canvas gizmo without
  // having to find the Guests popover first.
  useStore.setState((s) => {
    const ps = (s as unknown as { projectState: { crystals: CrystalState[]; chamberCrystal: CrystalState | null } })
      .projectState;
    fresh.name = uniqueCrystalName(fresh.name ?? hit.fallbackName, takenCrystalNames(ps));
    const guests = tpl.guestKey
      ? [
          {
            // Stable id so repeated template loads don't accumulate dupes
            // across the same browser session (the id replaces any prior
            // guest of this species at the chamber origin).
            id: `g_template_${tpl.guestKey}`,
            species: tpl.guestKey,
            // Origin placement — the user can drag with the on-canvas
            // gizmo. We don't run largestVoid here because the crystal
            // dynamics ref isn't mounted at template-load time.
            transform: { x: 0, y: 0, z: 0, euler: [0, 0, 0] as number[] },
          },
        ]
      : [];
    return {
      projectState: {
        ...(s as any).projectState,
        type: ProjectType.CRYSTAL_BUILDER,
        crystals: [...ps.crystals, fresh],
        selectedCrystal: fresh,
        selectedCrystalNames: fresh.name ? [fresh.name] : [],
        chamberCrystal: fresh,
        chamberGuests: guests,
        // NVT ensemble for "let the guest relax" templates. The thermostat
        // string defaults to 'berendsen' in createDefaultProjectState; we
        // only need to flip `constantTemperature` and set the target.
        ...(tpl.nvt
          ? {
              constantTemperature: true,
              temperature: tpl.targetTemperatureK ?? 300,
            }
          : {}),
      },
    } as never;
  });
  usePrimitiveStore.getState().setChanged(true);
};

// Nanostructure scene: cut the dot out of the host preset, add it to the
// gallery of a Nanomaterial Design project and load it into the lab (1x1x1 —
// a finite structure is never tiled) with the thermostat on.
const loadNanostructureTemplate = async (tpl: NanostructureTemplate): Promise<void> => {
  const hit = lookupInternalCrystal(tpl.hostPreset);
  if (!hit) {
    console.warn('Template: host crystal preset not found', tpl.hostPreset);
    return;
  }
  const host = hit.crystal;
  // Loaded on demand: AppCreator imports this module eagerly and no other
  // template needs the nanostructure geometry or the gallery actions.
  const [{ cutNanoparticle, toNanostructureCrystal }, { loadGalleryCrystalIntoLab }] = await Promise.all([
    import('../nano/nanostructures.ts'),
    import('../crystal/crystalGalleryActions.ts'),
  ]);
  const diameterAng = tpl.diameterNm * 10;
  const atoms = cutNanoparticle(host, { shape: tpl.shape, diameterAng, prune: !host.metallic });
  if (atoms.length === 0) {
    console.warn('Template: the nanoparticle cut holds no atoms', tpl);
    return;
  }
  const hostLabel = host.name ?? hit.fallbackName;
  const sizeText = `${tpl.diameterNm.toFixed(1)} nm`;
  const noun = host.metallic ? 'Nanoparticle' : 'Quantum Dot';
  const shapeSuffix = tpl.shape === 'sphere' ? '' : `, ${tpl.shape}`;
  const name = `${hostLabel} ${noun} (${sizeText}${shapeSuffix})`;
  const dot = toNanostructureCrystal(
    atoms,
    name,
    {
      kind: 'nanoparticle',
      host: hostLabel,
      shape: tpl.shape,
      diameterAng,
      description: `${hostLabel} ${noun.toLowerCase()}, ${sizeText} ${tpl.shape}`,
    },
    { metallic: host.metallic, elementCharges: host.elementCharges },
  );
  useStore.getState().set((state) => {
    const ps = state.projectState;
    ps.type = ProjectType.NANOMATERIAL_DESIGN;
    ps.crystals = [dot];
    ps.selectedCrystal = dot;
    ps.selectedCrystalNames = [name];
    // Open straight into chat (the AI-first surface): the levels, the color
    // and the size sweep this idea asks for are all chat tools.
    ps.galleryViewMode = GalleryViewMode.CHAT;
    ps.constantTemperature = true;
    ps.temperature = tpl.temperatureK;
  });
  // The shared loader: finite → 1x1x1, force field resolved as the lab does it.
  loadGalleryCrystalIntoLab(useStore.getState().projectState.crystals[0]);
  usePrimitiveStore.getState().setChanged(true);
};

// Strip the template param from the URL after we've consumed it, so a
// refresh doesn't re-trigger the loader (and so the URL bar doesn't carry
// stale state once the user starts editing).
export const clearTemplateFromUrl = () => {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('template')) {
      url.searchParams.delete('template');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    /* no-op */
  }
};

// Read the current ?template= value, or null. Caller validates against
// hasTemplate before running.
export const readTemplateFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('template');
  } catch {
    return null;
  }
};
